import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { formatAndSanitizeHTML } from 'utils/misc'
import { ISO8601_DATETIME_FORMATS } from 'utils/constants'
import { DatePicker, Input } from 'components/UIElements'
import SurveyQuestion from '../SurveyQuestion'
import MakeScrollable from '../MakeScrollable'
import QuestionFooter from '../QuestionFooter'
import {
  checkLogicAndComplete,
  getShouldRenderNextQuestion,
} from '../../utils/instrumentUtils'

const getLocalDateTimeFormatMap = (selectedLocale) => {
  const localeKey = selectedLocale || 'en'
  return moment().locale(localeKey).localeData()._longDateFormat
}

const formatToMomentDate = (date, nextDate) => {
  if (!nextDate) return ''
  const finalDate = date ? date.clone() : moment()
  return finalDate
    .year(nextDate.year())
    .month(nextDate.month())
    .date(nextDate.date())
}

const formatToMomentTime = (time, nextTime) => {
  if (!nextTime) return ''
  const finalTime = time ? time.clone() : moment()
  const newTimeMoment = moment(nextTime, moment.HTML5_FMT.TIME)
  return finalTime.hour(newTimeMoment.hour()).minute(newTimeMoment.minute())
}

const buildDateTimeAnswer = ({ label, id, selectedLocale, date, time }) => {
  if (!date || !time) return []
  const dateMoment = moment.isMoment(date) ? date : moment(date)
  const timeMoment = moment(time, moment.HTML5_FMT.TIME, true)
  if (!dateMoment.isValid() || !timeMoment.isValid()) return []

  let dateTime = moment()
  dateTime = formatToMomentDate(dateTime, dateMoment)
  dateTime = formatToMomentTime(dateTime, timeMoment.format('HH:mm'))

  const { L, LT } = getLocalDateTimeFormatMap(selectedLocale)
  const localDateTimeFormat = `${L} ${LT}`
  const value = `${dateTime.format(localDateTimeFormat)}, ${localDateTimeFormat}, ${dateTime.format(
    ISO8601_DATETIME_FORMATS.datetime,
  )}`

  return [
    {
      label,
      value,
      question: id,
    },
  ]
}

const completeDateTimeQuestion = (props, date, time) => {
  const {
    completeQuestion,
    id,
    nextQ,
    nextQId,
    question,
    selectedLocale,
    submissionSurvey,
    webSurvey,
    reviewFunctionalitySupported,
    isConsent = false,
    isLastQuestion = false,
    isSibling = false,
  } = props
  const { label, type, attributes } = question
  const { required } = attributes

  const answer = buildDateTimeAnswer({ label, id, selectedLocale, date, time })
  const submission = {
    id,
    label,
    name: id,
    type,
    required,
    state: date && time ? 'ANSWERED' : 'SKIPPED',
    answer
  }
  const currentAnswer = { [id]: answer }

  const shouldRenderNextQ = getShouldRenderNextQuestion({
    isConsent,
    isLastQuestion,
    isSibling,
  })

  checkLogicAndComplete(
    webSurvey,
    id,
    submissionSurvey,
    currentAnswer,
    nextQId,
    nextQ,
    completeQuestion,
    submission,
    reviewFunctionalitySupported,
    shouldRenderNextQ,
  )
}

function DateTimeType(props) {
  const [date, setDate] = useState('')
  const [time, setTime] = useState('')
  const timeRef = useRef()

  const {
    className,
    currentSurvey,
    id,
    isConsent,
    isLastQuestion,
    question,
    renderBackButton,
    setAllRequiredAnswered,
    selectedLocale,
    strings,
    storedAnswer,
    forwardedRef,
    submissionLoading,
    submissionSurvey,
    webSurvey,
    isSibling = false,
  } = props
  const { attributes, label } = question
  const { required } = attributes
  const _className = className ? ` ${className}` : ''
  const hasDateTime = date && time

  useEffect(() => {
    let isMounted = true

    if (storedAnswer?.[0]?.value) {
      const value = storedAnswer?.[0]?.value
      const parts = String(value)
        .split(',')
        .map((p) => p.trim())
        .filter(Boolean)
      const iso = parts[2] // stored as YYYY-MM-DD HH:mm
      const parsed = iso
        ? moment(iso, ISO8601_DATETIME_FORMATS.datetime, true)
        : moment.invalid()
      const momentDate = parsed.isValid()
        ? parsed.clone().startOf('day')
        : moment.invalid()
      const formattedTime = parsed.isValid() ? parsed.format('HH:mm') : ''

      if (isMounted) {
        if (parsed.isValid()) {
          onInput(true, momentDate)
          onInput(false, formattedTime)
        }
        // Complete immediately with the stored value
        if (!props.suppressAutoAdvance) {
          if (parsed.isValid())
            completeDateTimeQuestion(props, momentDate, formattedTime)
        }
      }
    }

    return () => {
      isMounted = false
    }
  }, [])

  const onInput = (isDate, value) => {
    setAllRequiredAnswered(false, id)
    const nextDate = isDate ? value : date
    const nextTime = isDate ? time : value
    if (isDate) {
      setDate(value)
    } else {
      setTime(value)
    }
    // Autosave into Redux so sibling pages see answers and can enable Next
    // (per-question footer is hidden when isSibling).
    try {
      const { question, webSurvey, completeQuestion } = props
      const { label, type, attributes } = question
      const { required } = attributes
      const answer = buildDateTimeAnswer({
        label,
        id,
        selectedLocale,
        date: nextDate,
        time: nextTime,
      })
      const submission = {
        id,
        label,
        name: id,
        type,
        required,
        state: nextDate && nextTime ? 'ANSWERED' : 'SKIPPED',
        answer,
      }
      if (typeof completeQuestion === 'function' && webSurvey) {
        completeQuestion(submission, webSurvey.order?.[0])
      }
    } catch (e) {
      console.warn('DateTimeType autosave failed', { error: e })
    }
  }

  return (
    <SurveyQuestion id={id}>
      <MakeScrollable>
        <div
          ref={forwardedRef}
          className={`survey-question datetime${_className}`}>
          <p className='question-label'>
            <span
              dangerouslySetInnerHTML={{
                __html: formatAndSanitizeHTML(label)
              }}
            />
          </p>
          <div className='input-row'>
            <DatePicker
              className='date-picker desktop-only'
              onDayChange={(val) => onInput(true, val || '')} // This prevents the uncontrolled input error if the val becomes undefined
              onEnterPress={() => timeRef.current.focus()}
              date={date}
              selectedLocale={selectedLocale}
            />
            <Input
              className='mobile-only'
              type='date'
              onChange={(val) => onInput(true, moment(val))}
              value={date && date.format('YYYY-MM-DD')}
            />
            <Input
              className='time-input'
              inputProps={{
                onKeyPress: (e) => {
                  if (e.key === 'Enter' && date && time) {
                    e.preventDefault()
                    completeDateTimeQuestion(props, date, time)
                  }
                }
              }}
              ref={timeRef}
              type='time'
              onChange={(val) => onInput(false, val)}
              value={time}
            />
          </div>
          {!isSibling && (
            <QuestionFooter
              completed={!!hasDateTime}
              id={id}
              isConsent={isConsent}
              isLastQuestion={isLastQuestion}
              loading={submissionLoading}
              onComplete={() => completeDateTimeQuestion(props, date, time)}
              question={question}
              renderBackButton={renderBackButton}
              strings={strings}
              submissionSurvey={submissionSurvey}
              webSurvey={webSurvey}
            />
          )}
        </div>
      </MakeScrollable>
    </SurveyQuestion>
  )
}

const ForwardedDateTimeType = React.forwardRef((props, ref) => (
  <DateTimeType {...props} forwardedRef={ref} />
))

export default ForwardedDateTimeType

DateTimeType.propTypes = {
  className: PropTypes.string,
  currentSurvey: PropTypes.arrayOf(PropTypes.string),
  id: PropTypes.string,
  question: PropTypes.object,
  renderBackButton: PropTypes.func,
  selectedLocale: PropTypes.string,
  setAllRequiredAnswered: PropTypes.func,
  strings: PropTypes.objectOf(PropTypes.string),
  forwardedRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) })
  ])
}
